import type { DeviceRegistryInterfaceItem } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { DeviceRegistrySaveBehavior } from '@tryiris-ai/common.protojs/api/v1/device_settings';

/**
 * Determines if a setting should be applied reactively (immediately).
 *
 * - SAVE_BEHAVIOR_UNSPECIFIED should be treated as AUTO_APPLY (reactive)
 * - SAVE_BEHAVIOR_AUTO_APPLY is explicitly reactive
 * - Only SAVE_BEHAVIOR_MODAL_SAVE requires confirmation
 *
 * @param item - The device registry interface item
 * @returns True if the setting should be applied immediately
 */
export const isReactiveSetting = (item: DeviceRegistryInterfaceItem): boolean => {
  const behavior = item.saveBehavior ?? DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_UNSPECIFIED;
  return (
    behavior === DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_UNSPECIFIED ||
    behavior === DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_AUTO_APPLY
  );
};

/**
 * Determines if a setting requires confirmation before applying.
 *
 * @param item - The device registry interface item
 * @returns True if the setting requires confirmation/modal save
 */
export const requiresConfirmation = (item: DeviceRegistryInterfaceItem): boolean => {
  return item.saveBehavior === DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_MODAL_SAVE;
};

/**
 * Gets a human-readable description of the save behavior.
 *
 * @param item - The device registry interface item
 * @returns Description of the save behavior
 */
export const getSaveBehaviorDescription = (item: DeviceRegistryInterfaceItem): string => {
  switch (item.saveBehavior) {
    case DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_AUTO_APPLY:
      return 'Auto-apply (immediate)';
    case DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_MODAL_SAVE:
      return 'Requires confirmation';
    case DeviceRegistrySaveBehavior.SAVE_BEHAVIOR_UNSPECIFIED:
    default:
      return 'Auto-apply (default)';
  }
};

/**
 * Type guard to check if save behavior is defined.
 *
 * @param item - The device registry interface item
 * @returns True if saveBehavior is explicitly set
 */
export const hasSaveBehavior = (item: DeviceRegistryInterfaceItem): boolean => {
  return item.saveBehavior !== undefined;
};
